import { useState } from "react";

/**
 * CC 工具调用卡片（assistant tool_use）
 * 折叠展示工具名 + 参数摘要，点击可展开详情。
 */
const TOOL_ICONS = {
  Bash: "⚡",
  Read: "📖",
  Write: "✏️",
  Edit: "🔧",
  MultiEdit: "🔧",
  Glob: "🔍",
  Grep: "🔎",
  LS: "📁",
  WebFetch: "🌐",
  WebSearch: "🔍",
  TodoWrite: "📋",
  Task: "🤖",
};

const summarizeInput = (toolName, input) => {
  if (!input) return "";
  if (toolName === "Bash" && input.command) return input.command.slice(0, 80);
  if ((toolName === "Read" || toolName === "Write" || toolName === "Edit" || toolName === "MultiEdit") && input.file_path) return input.file_path;
  if (toolName === "Glob" && input.pattern) return input.pattern;
  if (toolName === "Grep" && input.pattern) return input.pattern;
  if (toolName === "WebFetch" && input.url) return input.url.slice(0, 80);
  if (toolName === "WebSearch" && input.search_term) return input.search_term;
  const str = JSON.stringify(input);
  return str.length > 80 ? str.slice(0, 80) + "…" : str;
};

const FILE_TOOLS = new Set(["Read", "Write", "Edit", "MultiEdit"]);

const CCToolCallCard = ({ toolName, toolInput, elapsed_ms, onOpenFile }) => {
  const [expanded, setExpanded] = useState(false);
  const [openHint, setOpenHint] = useState("");
  const icon = TOOL_ICONS[toolName] || "🔧";
  const summary = summarizeInput(toolName, toolInput);
  const todos = Array.isArray(toolInput?.todos) ? toolInput.todos : [];
  const canOpen = onOpenFile && toolInput?.file_path && FILE_TOOLS.has(toolName);

  if (toolName === "TodoWrite" && todos.length > 0) {
    return (
      <div style={{
        background: "#161b22",
        border: "1px solid #30363d",
        borderLeft: "3px solid #58a6ff",
        borderRadius: "6px",
        padding: "8px 10px",
        margin: "4px 0",
        fontSize: "13px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span>📋</span>
          <span style={{ color: "#58a6ff", fontWeight: 600 }}>任务清单</span>
          <span style={{ color: "#6e7681", fontSize: 11 }}>{todos.length} 项</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {todos.map((todo, index) => (
            <div
              key={todo.id || index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: todo.status === "in_progress" ? "#10213a" : "#0d1117",
                border: `1px solid ${todo.status === "in_progress" ? "#1f6feb" : "#30363d"}`,
                borderRadius: 5,
                padding: "5px 7px",
                color: todo.status === "completed" ? "#8b949e" : "#d0d7de",
                textDecoration: todo.status === "completed" ? "line-through" : "none",
              }}
            >
              <span style={{ color: statusColor(todo.status), width: 14 }}>{statusIcon(todo.status)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{todo.content || todo.activeForm}</span>
              <span style={{ color: statusColor(todo.status), fontSize: 11 }}>{statusLabel(todo.status)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setExpanded((v) => !v)}
      style={{
        background: "#161b22",
        border: "1px solid #30363d",
        borderRadius: "6px",
        padding: "6px 12px",
        margin: "4px 0",
        cursor: "pointer",
        fontSize: "13px",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span>{icon}</span>
        <span style={{ color: "#58a6ff", fontWeight: 500 }}>{toolName}</span>
        {summary && (
          <span style={{ color: "#8b949e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {summary}
          </span>
        )}
        {canOpen && (
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              setOpenHint("打开中…");
              try {
                const result = await onOpenFile(toolInput.file_path);
                if (result && result.ok === false) setOpenHint(result.error || "打开失败");
                else setOpenHint("");
              } catch (err) {
                setOpenHint(err.message || "打开失败");
              }
            }}
            title={openHint || "在编辑器中打开"}
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              border: "1px solid #30363d",
              background: "#0d1117",
              color: openHint && openHint !== "打开中…" ? "#f85149" : "#58a6ff",
              borderRadius: 4,
              fontSize: 11,
              padding: "1px 6px",
              cursor: "pointer",
            }}
          >
            {openHint && openHint !== "打开中…" ? "失败" : "打开"}
          </button>
        )}
        {elapsed_ms != null && (
          <span style={{ color: "#6e7681", fontSize: "11px", marginLeft: "auto", flexShrink: 0 }}>
            {(elapsed_ms / 1000).toFixed(1)}s
          </span>
        )}
        <span style={{ color: "#6e7681", fontSize: "11px" }}>{expanded ? "▲" : "▼"}</span>
      </div>
      {expanded && (
        <pre style={{
          marginTop: "8px",
          padding: "8px",
          background: "#0d1117",
          borderRadius: "4px",
          fontSize: "11px",
          color: "#e6edf3",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          maxHeight: "200px",
          overflowY: "auto",
        }}>
          {JSON.stringify(toolInput, null, 2)}
        </pre>
      )}
    </div>
  );
};

const statusIcon = (status) => {
  if (status === "completed") return "✓";
  if (status === "in_progress") return "●";
  if (status === "cancelled") return "×";
  return "○";
};

const statusLabel = (status) => {
  if (status === "completed") return "已完成";
  if (status === "in_progress") return "进行中";
  if (status === "cancelled") return "已取消";
  return "待处理";
};

const statusColor = (status) => {
  if (status === "completed") return "#3fb950";
  if (status === "in_progress") return "#58a6ff";
  if (status === "cancelled") return "#f85149";
  return "#8b949e";
};

export default CCToolCallCard;
